import { useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../context/Auth/AuthContext';
import { SocketContext } from '../context/Socket/SocketContext';
import toastUtils from '../utils/toastUtils';
import { i18n } from '../translate/i18n';

export const useKanbanSocket = (onTaskUpdate, onTaskCreate, onTaskDelete, onTaskMove) => {
  const { user, socket: authSocket } = useContext(AuthContext);
  const socketContext = useContext(SocketContext);
  
  // Use socket from AuthContext if SocketContext is not available
  const socket = socketContext?.socket || authSocket;

  // Handler para eventos de tarefas
  const handleKanbanTaskEvent = useCallback((data) => {
    const { action, task, tasks, ticketId } = data;

    switch (action) {
      case 'create':
        if (onTaskCreate) {
          onTaskCreate(task);
        }
        // Não mostrar notificação se foi o próprio usuário que criou
        if (task.createdBy !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.taskCreated'), 'Task Criada');
        }
        break;

      case 'update':
        if (onTaskUpdate) {
          onTaskUpdate(task);
        }
        if (task.updatedBy !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.taskUpdated'), 'Status Alterado');
        }
        break;

      case 'delete':
        if (onTaskDelete) {
          onTaskDelete(task.id);
        }
        if (task.deletedBy !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.taskDeleted'), 'Status Alterado');
        }
        break;

      case 'move':
      case 'reorder':
        if (onTaskMove) {
          onTaskMove(task);
        }
        if (task.updatedBy !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.taskMoved'), 'Card Movido');
        }
        break;

      case 'bulk_update':
        if (onTaskUpdate && tasks) {
          tasks.forEach(task => onTaskUpdate(task));
        }
        toastUtils.info(i18n.t('kanban.messages.bulkUpdateSuccess'), 'Status Alterado');
        break;

      case 'time_updated':
        if (onTaskUpdate) {
          onTaskUpdate(task);
        }
        if (task.updatedBy !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.progressUpdated'), 'Status Alterado');
        }
        break;

      case 'assigned':
        if (onTaskUpdate) {
          onTaskUpdate(task);
        }
        // Notificar apenas o usuário que foi atribuído
        if (task.assigneeId === user.id && task.updatedBy !== user.id) {
          toastUtils.success(i18n.t('kanban.messages.taskAssignedToYou', {
            title: task.title
          }), 'Task Criada');
        }
        break;

      case 'status_changed':
        if (onTaskUpdate) {
          onTaskUpdate(task);
        }
        if (task.updatedBy !== user.id) {
          const statusLabel = i18n.t(`kanban.tasks.status.${task.status}`);
          toastUtils.info(i18n.t('kanban.messages.statusChanged', {
            title: task.title,
            status: statusLabel
          }), 'Status Alterado');
        }
        break;

      default:
        console.log('Unhandled kanban task event:', action);
    }
  }, [onTaskUpdate, onTaskCreate, onTaskDelete, onTaskMove, user?.id]);

  // Handler para eventos de automação
  const handleKanbanAutomationEvent = useCallback((data) => {
    const { action, automation, execution } = data;

    switch (action) {
      case 'triggered':
        // Automação foi executada
        if (execution && execution.success) {
          toastUtils.success(i18n.t('kanban.messages.automationTriggered', {
            name: automation.name
          }), 'Status Alterado');
        } else if (execution && !execution.success) {
          toastUtils.error(i18n.t('kanban.messages.automationFailed', {
            name: automation.name,
            error: execution.error
          }), 'Erro no Kanban');
        }
        break;

      case 'enabled':
        toastUtils.info(i18n.t('kanban.messages.automationEnabled', {
          name: automation.name
        }), 'Status Alterado');
        break;

      case 'disabled':
        toastUtils.info(i18n.t('kanban.messages.automationDisabled', {
          name: automation.name
        }), 'Status Alterado');
        break;

      default:
        console.log('Unhandled kanban automation event:', action);
    }
  }, []);

  // Handler para eventos de analytics
  const handleKanbanAnalyticsEvent = useCallback((data) => {
    const { action, analytics } = data;

    switch (action) {
      case 'milestone_reached':
        toastUtils.success(i18n.t('kanban.messages.milestoneReached', {
          milestone: analytics.milestone,
          value: analytics.value
        }), 'Status Alterado');
        break;

      case 'deadline_approaching':
        toastUtils.warning(i18n.t('kanban.messages.deadlineApproaching', {
          count: analytics.count,
          timeframe: analytics.timeframe
        }), 'Erro no Kanban');
        break;

      case 'performance_alert':
        if (analytics.type === 'productivity_drop') {
          toastUtils.warning(i18n.t('kanban.messages.productivityAlert'), 'Erro no Kanban');
        } else if (analytics.type === 'overdue_increase') {
          toastUtils.error(i18n.t('kanban.messages.overdueAlert', {
            count: analytics.count
          }), 'Erro no Kanban');
        }
        break;

      default:
        console.log('Unhandled kanban analytics event:', action);
    }
  }, []);

  // Configurar listeners do socket
  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyRoom = `company${user.companyId}`;
    
    // Listeners para eventos do Kanban
    socket.on(`${companyRoom}-kanban-task`, handleKanbanTaskEvent);
    socket.on(`${companyRoom}-kanban-automation`, handleKanbanAutomationEvent);
    socket.on(`${companyRoom}-kanban-analytics`, handleKanbanAnalyticsEvent);

    // Entrar na sala da empresa para receber atualizações
    socket.emit('joinCompanyRoom', user.companyId);

    // Cleanup
    return () => {
      socket.off(`${companyRoom}-kanban-task`, handleKanbanTaskEvent);
      socket.off(`${companyRoom}-kanban-automation`, handleKanbanAutomationEvent);
      socket.off(`${companyRoom}-kanban-analytics`, handleKanbanAnalyticsEvent);
    };
  }, [
    socket, 
    user?.companyId, 
    handleKanbanTaskEvent, 
    handleKanbanAutomationEvent,
    handleKanbanAnalyticsEvent
  ]);

  // Função para emitir eventos do Kanban
  const emitKanbanEvent = useCallback((eventType, data) => {
    if (!socket || !user?.companyId) return;

    const companyRoom = `company${user.companyId}`;
    socket.emit(`${companyRoom}-${eventType}`, {
      ...data,
      userId: user.id,
      companyId: user.companyId,
      timestamp: new Date().toISOString()
    });
  }, [socket, user?.id, user?.companyId]);

  // Emitir evento de usuário online/offline no Kanban
  const updateKanbanPresence = useCallback((isOnline) => {
    emitKanbanEvent('kanban-presence', {
      userId: user?.id,
      isOnline,
      location: 'kanban'
    });
  }, [emitKanbanEvent, user?.id]);

  // Emitir evento de tarefa sendo visualizada
  const viewTask = useCallback((taskId) => {
    emitKanbanEvent('kanban-task-view', {
      taskId,
      userId: user?.id
    });
  }, [emitKanbanEvent, user?.id]);

  // Emitir evento de digitação em comentários
  const typingInTask = useCallback((taskId, isTyping) => {
    emitKanbanEvent('kanban-task-typing', {
      taskId,
      userId: user?.id,
      isTyping
    });
  }, [emitKanbanEvent, user?.id]);

  return {
    emitKanbanEvent,
    updateKanbanPresence,
    viewTask,
    typingInTask
  };
};

export default useKanbanSocket;